import {
	CatalogueAppointmentType,
	CatalogueProduct,
	CatalogueProductsResponse,
	CatalogueVariant,
} from "../../../types/finance/CatalogueTypes";

/**
 * The pure half of the invoice line-item catalogue picker (S11) — the debounced, race-guarded
 * product search runner, the option builders, and the one function that classifies an empty list.
 *
 * Extracted from the `.tsx` for the same two reasons `patientSearch.ts` was, and this file is a
 * deliberate port of it. The first is the usual one: the stale-response rule below is the whole
 * point of the control and it deserves a test that cannot be flaky. The second is concrete —
 * **antd v5's `Select` dropdown cannot be opened under jsdom**: its generated stylesheet contains a
 * selector nwsapi rejects (`'*+.ant-select-item-option-selected:not(…)' is not a valid selector`),
 * which surfaces as an uncaught `SyntaxError` during render the moment a search opens the list. So
 * the behaviour is tested here, where there is no DOM at all, rather than through the widget.
 *
 * ⚠️ **Nothing in here converts money.** `unitPriceCents` arrives as integer cents from finance
 * (the one money boundary, S6) and is carried through untouched — including `null`, which is a
 * genuinely unpriced item and must never become `0`. `utils/finance/money.ts::dollarsToCents`
 * returns 0 for input it cannot parse; pointing it at a catalogue price would turn "unpriced" into
 * "$0.00", which reads to a patient as "nothing owed". Render a price with `formatCents`, which
 * already produces an em dash for `null`.
 */

export const CATALOGUE_SEARCH_DEBOUNCE_MS = 300;

/** A count that can be rendered. Anything non-numeric collapses to 0 rather than reaching a label. */
function toCount(value: number | null | undefined): number {
	const asNumber = Number(value);
	return Number.isFinite(asNumber) ? Math.trunc(asNumber) : 0;
}

// ══ The search runner ═════════════════════════════════════════════════════════════════════════

export interface ProductSearchResult {
	products: CatalogueProduct[];
	/**
	 * Finance's upstream call FAILED (S7's degrade). Not the same thing as an empty catalogue — see
	 * `catalogueEmptyState`.
	 */
	unavailable: boolean;
}

export interface ProductSearchRunner {
	/** Debounced. A blank term is a legitimate search — see `search()` below. */
	search(term: string): void;
	/** Cancel the pending debounce and invalidate every in-flight response. */
	dispose(): void;
}

export interface ProductSearchRunnerOptions {
	fetch: (term: string) => Promise<CatalogueProductsResponse>;
	onResults: (result: ProductSearchResult) => void;
	onSearchingChange: (searching: boolean) => void;
	debounceMs?: number;
}

/**
 * Debounce + **stale-response guard**.
 *
 * ⚠️ The debounce alone is not enough, and assuming it is has burned this codebase before (the
 * commerce order-search S7 regression). A debounce bounds how OFTEN a request is issued; it says
 * nothing about the order the responses come back in. Type "vit", pause past the debounce, type
 * "vitamin" — two requests are in flight, and if the slow "vit" response lands after the fast
 * "vitamin" one it silently replaces the correct list with the wrong one, and the practitioner
 * bills a patient for whatever they picked off it.
 *
 * So every search takes a monotonically increasing ticket, and only the LATEST ticket may write:
 * results, the degrade flag, and the searching flag. `dispose()` burns a ticket too, so a response
 * arriving after unmount cannot call `setState` on a dead component.
 *
 * A failed lookup deliberately writes NOTHING — blanking the list under a practitioner who is
 * mid-way through choosing is worse than leaving the previous results up. Note this is the
 * *transport* failing; finance reporting that COMMERCE failed is a 200 carrying `unavailable`, and
 * that does get written.
 *
 * ⚠️ **`patientSearch`'s blank-term guard is deliberately NOT ported.** It exists there because a
 * blank patient search returns every patient in the clinic. `search` is optional on
 * `GET /catalogue/products` and omitting it returns the first page of the whole active catalogue,
 * which is exactly what a freshly-opened picker should show — a practitioner must not have to type
 * to discover that products exist. The fetcher drops an empty `search` from the query string.
 */
export function createProductSearchRunner(
	options: ProductSearchRunnerOptions,
): ProductSearchRunner {
	const debounceMs = options.debounceMs ?? CATALOGUE_SEARCH_DEBOUNCE_MS;

	let timer: ReturnType<typeof setTimeout> | null = null;
	let sequence = 0;

	const run = async (term: string) => {
		sequence += 1;
		const ticket = sequence;

		options.onSearchingChange(true);

		try {
			const response = await options.fetch(term);
			if (ticket !== sequence) {
				return;
			}
			options.onResults({
				products: response?.data ?? [],
				unavailable: response?.unavailable === true,
			});
		} catch {
			// Intentionally silent: keep whatever is on screen. The request itself is logged by
			// the axios layer; there is nothing actionable to say inside a dropdown.
		} finally {
			if (ticket === sequence) {
				options.onSearchingChange(false);
			}
		}
	};

	return {
		search(term: string) {
			if (timer) {
				clearTimeout(timer);
			}

			const trimmed = (term ?? "").trim();
			timer = setTimeout(() => run(trimmed), debounceMs);
		},

		dispose() {
			if (timer) {
				clearTimeout(timer);
				timer = null;
			}
			sequence += 1;
		},
	};
}

// ══ Availability ══════════════════════════════════════════════════════════════════════════════

/**
 * `12` -> `"12 available"`, `0` -> `"out of stock"`, `-3` -> `"out of stock"`.
 *
 * ⚠️ A negative count is REAL and reachable: parent decision D2 is that insufficient stock warns
 * and proceeds, so an over-sold variant genuinely goes negative. `"-3 available"` in a dropdown is
 * nonsense to read; the shortfall belongs in S13's warning on issue, not in a picker row.
 */
export function formatAvailability(available: number | null | undefined): string {
	const count = toCount(available);
	return count > 0 ? `${count} available` : "out of stock";
}

/** A product's availability is the sum of its variants' — stock is keyed by variant, not product. */
export function productAvailability(product: CatalogueProduct): number {
	return (product?.variants ?? []).reduce((sum, variant) => sum + toCount(variant.available), 0);
}

// ══ Options ═══════════════════════════════════════════════════════════════════════════════════

export interface CatalogueProductOption {
	value: string;
	label: string;
	title?: string;
	product?: CatalogueProduct;
}

export interface CatalogueVariantOption {
	value: string;
	label: string;
	title?: string;
	/** Integer cents, carried through untouched. `null` is unpriced and must stay `null`. */
	unitPriceCents: number | null;
	available: number;
	variant?: CatalogueVariant;
}

export interface CatalogueAppointmentTypeOption {
	value: string;
	label: string;
	/** Integer cents. `null` is the COMMON case here — most types are unpriced. */
	unitPriceCents: number | null;
	appointmentType?: CatalogueAppointmentType;
}

/** `"Vitamin D3 5000IU — VITD3-60 · 12 available"`. The SKU segment is dropped when there is none. */
export function productLabel(product: CatalogueProduct): string {
	const name = (product.name ?? "").trim() || `Product ${product.productId}`;
	const sku = (product.sku ?? "").trim();
	const head = sku ? `${name} — ${sku}` : name;

	return `${head} · ${formatAvailability(productAvailability(product))}`;
}

/** `"60 capsules · 7 available"`. Falls back through title -> sku -> id. */
export function variantLabel(variant: CatalogueVariant): string {
	const name =
		(variant.title ?? "").trim() || (variant.sku ?? "").trim() || `Variant ${variant.variantId}`;

	return `${name} · ${formatAvailability(variant.available)}`;
}

/** `"Initial consultation · 60 min"`. An unset duration drops the segment rather than showing 0. */
export function appointmentTypeLabel(type: CatalogueAppointmentType): string {
	const name = (type.name ?? "").trim() || `Appointment type ${type.appointmentTypeId}`;
	const minutes = toCount(type.durationMinutes);

	return minutes > 0 ? `${name} · ${minutes} min` : name;
}

/**
 * Build the product option list.
 *
 * Two rows are prepended when they would otherwise be missing, for the same reason
 * `buildPatientOptions` prepends them — antd renders the raw value as the label when no option
 * matches, and a bare numeric product id in the field reads as a bug rather than as a product:
 *
 *   - the currently `selected` product, once a later search has pushed it out of the results;
 *   - a bare `Product <id>` placeholder for a `value` that arrived from outside (editing an
 *     existing draft invoice line, before any search has run).
 */
export function buildProductOptions(
	products: CatalogueProduct[],
	selected: CatalogueProduct | null,
	value?: string,
): CatalogueProductOption[] {
	const rows = [...(products ?? [])];

	if (selected && !rows.some((p) => String(p.productId) === String(selected.productId))) {
		rows.unshift(selected);
	}

	const options: CatalogueProductOption[] = rows.map((product) => ({
		value: String(product.productId),
		label: productLabel(product),
		title: (product.sku ?? "").trim() || undefined,
		product,
	}));

	if (value && !options.some((option) => option.value === String(value))) {
		options.unshift({ value: String(value), label: `Product ${value}` });
	}

	return options;
}

/**
 * The selected product's variants, one option each.
 *
 * No filtering happens here: finance's projection is already active-only (commerce filters before
 * it), so a client-side `isActive` pass would be re-filtering a constant `true`.
 *
 * ⚠️ **There is no `isDefaultVariant` to preserve.** The S11 brief asked for one so the widget
 * could preselect, but S7 omits it from the projection ON PURPOSE — parent decision D6 is that the
 * picker asks for product AND variant, both explicit, because a silent preselection of the wrong
 * SKU bills the patient for the wrong thing and moves the wrong stock. Do not synthesise one from
 * `variants[0]`; if preselection is genuinely wanted, the field has to be added to the server
 * projection first. The whole `variant` rides on the option, so anything added there arrives here.
 */
export function buildVariantOptions(
	product: CatalogueProduct | null | undefined,
): CatalogueVariantOption[] {
	return (product?.variants ?? []).map((variant) => ({
		value: String(variant.variantId),
		label: variantLabel(variant),
		title: (variant.sku ?? "").trim() || undefined,
		// `?? null` normalises a missing key, and leaves 0 as 0 — a genuinely free item is not
		// the same as an unpriced one.
		unitPriceCents: variant.unitPriceCents ?? null,
		available: toCount(variant.available),
		variant,
	}));
}

/**
 * The appointment types, one option each. There is no search and no pagination — the list is small
 * and finance serves it from a 60s cache — so there is no "keep the selected row visible" case to
 * handle the way `buildProductOptions` has to.
 */
export function buildAppointmentTypeOptions(
	types: CatalogueAppointmentType[],
): CatalogueAppointmentTypeOption[] {
	return (types ?? []).map((type) => ({
		value: String(type.appointmentTypeId),
		label: appointmentTypeLabel(type),
		unitPriceCents: type.unitPriceCents ?? null,
		appointmentType: type,
	}));
}

// ══ The three empty states ════════════════════════════════════════════════════════════════════

/**
 * An empty option list means one of THREE different things and the widget must not render them
 * with the same message.
 *
 * | state | what happened | what it should say |
 * |---|---|---|
 * | `unavailable` | finance's call to commerce/healthcare failed (200 + `unavailable: true`) | "Catalogue unavailable — enter the line manually" |
 * | `empty` | the clinic has no catalogue wired up, or none active. NO flag, because a retry cannot fix it | "No products in the catalogue" |
 * | `no-matches` | the catalogue answered, the search term matched nothing | "No matches for …" |
 *
 * ⚠️ **`unavailable` is the likely state on a clinic today**, because `access-finance` is not yet
 * registered on finance's OAuth client, so both upstream calls 403 and both endpoints degrade.
 * Collapsing it into `empty` would tell a practitioner their clinic sells nothing.
 */
export type CatalogueEmptyState = "unavailable" | "empty" | "no-matches";

export function catalogueEmptyState(
	optionCount: number,
	unavailable: boolean,
	searchTerm?: string,
): CatalogueEmptyState | null {
	if (optionCount > 0) {
		return null;
	}

	if (unavailable) {
		return "unavailable";
	}

	return (searchTerm ?? "").trim() ? "no-matches" : "empty";
}
